import { Alert } from "@opowiedzo/ui";
import { useCustomMutation, useGetIdentity, useInvalidate, useTable } from "@refinedev/core";
import { Link } from "react-router";

import { Badge } from "@/components/Badge";
import { SmallButton } from "@/components/Form";
import { RetryIcon } from "@/components/Icon";
import { ErrorBox, FilterBar, Loading, PageHeader, Pagination, Select } from "@/components/Page";
import { EmptyRow, Table, Td, Th, Tr } from "@/components/Table";
import {
  TRANSCRIPT_STATUS_OPTIONS,
  formatDateTime,
  recordingStatus,
  transcriptStatus,
} from "@/lib/format";
import { filterValue } from "@/lib/query";
import type { AdminIdentity } from "@/providers/authProvider";
import type { RecordingRow } from "./show";

export function TranscriptionsPage() {
  const invalidate = useInvalidate();
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const { mutate, mutation } = useCustomMutation();

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<RecordingRow>({
      resource: "recordings",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
      // Ten ekran otwiera się po to, żeby zobaczyć, co się zacięło.
      filters: { initial: [{ field: "problems_only", operator: "eq", value: "true" }] },
    });

  const mozeDzialac = (identity?.permissions ?? []).includes("stories.act");

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  const ponow = (recordingId: string) =>
    mutate(
      {
        url: `/recordings/${recordingId}/retry-transcription`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => invalidate({ resource: "recordings", invalidates: ["list"] }) },
    );

  return (
    <>
      <PageHeader
        title="Kolejka transkrypcji"
        description="Nagrania czekające na przepisanie i te, którym się nie udało. Bez klucza AI nic nie ginie - nagranie leży gotowe i wystarczy je ponowić."
      />

      <FilterBar>
        <Select
          label="Stan transkrypcji"
          value={filterValue(filters, "transcript_status")}
          options={TRANSCRIPT_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("transcript_status", value)}
        />
        <Select
          label="Zakres"
          value={filterValue(filters, "problems_only")}
          emptyLabel="Wszystkie nagrania"
          options={[{ value: "true", label: "Tylko z problemem" }]}
          onChange={(value) => ustawFiltr("problems_only", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />
      {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Nagrane</Th>
                <Th>Opowieść</Th>
                <Th>Plik</Th>
                <Th>Transkrypcja</Th>
                <Th>Powód</Th>
                <Th align="right">Akcja</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Nic nie stoi w kolejce. Wszystko przepisane.</EmptyRow>
              )}
              {result.data.map((recording) => {
                const plik = recordingStatus(recording.status);
                const tekst = transcriptStatus(recording.transcript_status);
                return (
                  <Tr key={recording.id}>
                    <Td nowrap>{formatDateTime(recording.created_at)}</Td>
                    <Td nowrap>
                      <Link
                        to={`/opowiesci/${recording.memoir_id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {recording.memoir_title ?? "Opowieść"}
                      </Link>
                    </Td>
                    <Td>
                      <Badge tone={plik.tone}>{plik.label}</Badge>
                    </Td>
                    <Td>
                      <Badge tone={tekst.tone}>{tekst.label}</Badge>
                    </Td>
                    <Td>
                      <span className="text-ink-soft">
                        {recording.transcript_error ?? recording.failure_reason ?? "-"}
                      </span>
                    </Td>
                    <Td align="right">
                      {mozeDzialac &&
                        recording.status === "ready" &&
                        recording.transcript_status !== "done" && (
                          <SmallButton
                            disabled={mutation.isPending}
                            onClick={() => ponow(recording.id)}
                          >
                            <RetryIcon className="size-3.5 shrink-0" />
                            Ponów
                          </SmallButton>
                        )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
